import React, { useState, useEffect, useRef } from 'react';
import {
    Highlight,
    Popup,
    AreaHighlight,
    PdfHighlighter,
    PdfLoader,
} from '../../custom-libs/react-pdf-highlighter/src/index';
import AnnotContext from '../AnnotContext';
import './annotation-layer.scss';

// Import TextToSpeech from Capacitor plugin if available
let TextToSpeech;
if (window.Capacitor && window.Capacitor.isPluginAvailable('TextToSpeech')) {
    TextToSpeech = require('@capacitor-community/text-to-speech').TextToSpeech;
}

// Define the AnnotationLayer component
function AnnotationLayer({ loadedPdf, annotState, SD_pdfPropts, SD_tip }) {
    const [hash, setHash] = useState(null);
    const scrollViewerTo = useRef();
    const ttsUtterance = useRef(null); // Ref to store current TTS utterance

    // TTS function using Capacitor plugin or browser's native SpeechSynthesis as fallback
    const speakText = async (text) => {
        try {
            if (TextToSpeech) {
                // If a previous utterance is still running, stop it.
                if (ttsUtterance.current) {
                    await TextToSpeech.stop();
                }
                ttsUtterance.current = await TextToSpeech.speak({ text });
            } else if (window.speechSynthesis) {
                // Cancel any ongoing speech
                window.speechSynthesis.cancel();
                // Create a new utterance
                const utterance = new SpeechSynthesisUtterance(text);
                // Optionally, set language or other properties (e.g., 'en-US')
                utterance.lang = 'en-US';
                // When speaking finishes, clear the reference
                utterance.onend = () => {
                    ttsUtterance.current = null;
                };
                window.speechSynthesis.speak(utterance);
                ttsUtterance.current = utterance;
            } else {
                console.warn('No TTS capability available on this device.');
            }
        } catch (error) {
            console.error('Error while speaking text:', error);
        }
    };

    // Cleanup function to stop TTS
    const stopTTS = async () => {
        if (ttsUtterance.current) {
            try {
                if (TextToSpeech) {
                    await TextToSpeech.stop();
                } else if (window.speechSynthesis) {
                    window.speechSynthesis.cancel();
                }
            } catch (error) {
                console.error('Error while stopping TTS:', error);
            }
            ttsUtterance.current = null;
        }
    };

    // Define the HighlightPopup component that triggers TTS on mount
    const HighlightPopup = ({ comment }) => {
        useEffect(() => {
            if (comment) {
                const textToSpeak = comment.assetDescription
                    ? comment.icon
                        ? `${comment.assetDescription} ${comment.icon}`
                        : comment.assetDescription
                    : '';
                if (textToSpeak) {
                    speakText(textToSpeak);
                }
            }
            return () => {
                stopTTS(); // Cleanup TTS when unmounting or hiding the tooltip
            };
        }, [comment]);

        return comment && comment.assetDescription ? (
            <div className="Highlight__popup">{comment.assetDescription}</div>
        ) : null;
    };

    // Helper functions for handling highlight hashes
    const parseIdFromHash = () => document.location.hash.slice(`#kID-`.length);

    const resetHash = () => {
        document.location.hash = '';
    };

    const scrollToHighlightFromHash = () => {
        const newHash = parseIdFromHash();
        let highlight = null;
        if (newHash !== hash) {
            setHash(newHash);
            highlight = getHighlightById(newHash);
        }
        if (highlight) {
            scrollViewerTo.current(highlight);
            SD_pdfPropts.action({
                type: 'chng-active-annot',
                data: highlight,
            });
        }
    };

    const getHighlightById = (id) => {
        return annotState?.find((highlight) => highlight.id === id);
    };

    useEffect(() => {
        window.addEventListener('hashchange', scrollToHighlightFromHash, false);
        return () => {
            window.removeEventListener('hashchange', scrollToHighlightFromHash);
        };
    }, [annotState]);

    useEffect(() => {
        return () => {
            // Cleanup TTS when unmounting the component
            stopTTS();
        };
    }, []);

    return (
        <PdfLoader url={loadedPdf}>
            {(pdfDoc) => (
                <PdfHighlighter
                    pdfDocument={pdfDoc}
                    enableAreaSelection={() => SD_pdfPropts?.state?.mode?.annot}
                    onScrollChange={resetHash}
                    scrollRef={(scrollTo) => {
                        scrollViewerTo.current = scrollTo;
                        scrollToHighlightFromHash();
                    }}
                    onSelectionFinished={(
                        position,
                        content,
                        hideTipAndSelection,
                        transformSelection
                    ) =>
                        SD_tip?.action({
                            type: 'init',
                            data: {
                                showTip: true,
                                props: {
                                    position: position,
                                    content: content,
                                    hideTipAndSelection: hideTipAndSelection,
                                    transformSelection: transformSelection,
                                },
                            },
                        })
                    }
                    highlightTransform={(
                        highlight,
                        index,
                        setTip,
                        hideTip,
                        viewportToScaled,
                        screenshot,
                        isScrolledTo
                    ) => {
                        const isTextHighlight = !Boolean(
                            highlight.content && highlight.content.image
                        );
                        const component = isTextHighlight ? (
                            <Highlight
                                isScrolledTo={isScrolledTo}
                                position={highlight.position}
                                comment={highlight.comment}
                            />
                        ) : (
                            <AnnotContext.Provider value={SD_pdfPropts}>
                                <AreaHighlight
                                    isScrolledTo={isScrolledTo}
                                    highlight={highlight}
                                    onChange={() => {}}
                                />
                            </AnnotContext.Provider>
                        );

                        return (
                            <Popup
                                popupContent={
                                    <HighlightPopup comment={highlight.comment} />
                                }
                                onMouseOver={(popupContent) => {
                                    setTip(
                                        highlight,
                                        (highlight) => popupContent
                                    );
                                }}
                                onMouseOut={() => {
                                    hideTip();
                                    stopTTS(); // Stop TTS when hiding the tooltip
                                }}
                                key={index}
                                children={component}
                            />
                        );
                    }}
                    highlights={annotState}
                />
            )}
        </PdfLoader>
    );
}

export default AnnotationLayer;
